import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Mail, Phone, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Reveal } from "./Reveal";
import { useConsent, openCookiePreferences } from "../lib/consent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { DIVISIONI, PRESELECT_EVENT } from "../data/divisioni";
import { validateContact, suggestEmail } from "../lib/contactValidation";

const EMPTY = { nome: "", azienda: "", email: "", telefono: "", divisione: "", messaggio: "" };

const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-[#0B1F3A] outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-slate-400 focus:border-[#0B1F3A] focus:ring-4 focus:ring-[#0B1F3A]/[0.07]";

export const Contatti = () => {
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const [privacyOk, setPrivacyOk] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const emailHint = suggestEmail(form.email);

    useEffect(() => {
        const onPreselect = (e) => setForm((f) => ({ ...f, divisione: e.detail }));
        window.addEventListener(PRESELECT_EVENT, onPreselect);
        // Arrivo da una pagina Realizzazioni (/?divisione=...#contatti): preseleziona la divisione del lavoro.
        // Differito: se il valore viene impostato nello stesso tick del mount, il Select (Radix) non mostra l'etichetta.
        const wanted = new URLSearchParams(window.location.search).get("divisione");
        let timer;
        if (wanted) {
            const key = wanted.toLowerCase().slice(0, 8);
            const match = DIVISIONI.find((d) => d.toLowerCase().startsWith(key));
            if (match) timer = setTimeout(() => setForm((f) => ({ ...f, divisione: match })), 150);
        }
        return () => { clearTimeout(timer); window.removeEventListener(PRESELECT_EVENT, onPreselect); };
    }, []);
    const { consent } = useConsent();

    const set = (k) => (e) => {
        setForm({ ...form, [k]: e.target.value });
        if (fieldErrors[k]) setFieldErrors({ ...fieldErrors, [k]: undefined });
    };
    const FieldError = ({ k }) =>
        fieldErrors[k] ? (
            <p className="mt-1.5 text-xs font-semibold text-[#B91C1C]" role="alert" data-testid={`contact-error-${k}`}>{fieldErrors[k]}</p>
        ) : null;

    const submit = async (e) => {
        e.preventDefault();
        const errs = validateContact(form);
        setFieldErrors(errs);
        if (Object.keys(errs).length) {
            setError("Controlli i campi evidenziati in rosso.");
            return;
        }
        if (!privacyOk) {
            setError("Per inviare la richiesta devi confermare di aver letto la Privacy Policy.");
            return;
        }
        setSending(true);
        setError("");
        try {
            await axios.post(`${API}/contact`, form);
            setSent(true);
        } catch (err) {
            const details = err?.response?.data?.detail;
            if (Array.isArray(details)) {
                const mapped = {};
                details.forEach((d) => { mapped[d.loc?.[d.loc.length - 1]] = String(d.msg).replace(/^Value error, /, ""); });
                setFieldErrors(mapped);
                setError("Controlli i campi evidenziati in rosso.");
                return;
            }
            setError("L'invio non è riuscito. I dati inseriti sono stati conservati: riprova tra qualche istante oppure scrivici a info@ftmgroup.it.");
        } finally {
            setSending(false);
        }
    };

    const reset = () => {
        setForm(EMPTY);
        setPrivacyOk(false);
        setError("");
        setFieldErrors({});
        setSent(false);
    };

    return (
        <section
            id="contatti"
            data-testid="contatti-section"
            className="bg-[#F2F5F9] py-20 lg:py-28"
        >
            <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
                <div className="max-w-2xl">
                    <Reveal>
                        <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#E30613]">
                            Contatti
                        </span>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-[#0B1F3A] sm:text-4xl">
                            Parliamo del tuo progetto.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.18}>
                        <p className="mt-5 text-base leading-relaxed text-[#475569] sm:text-lg">
                            Sopralluogo e preventivo gratuiti. Rispondiamo nel più breve tempo
                            possibile.
                        </p>
                    </Reveal>
                </div>

                <div className="mt-14 grid gap-8 lg:grid-cols-12">
                    <Reveal className="lg:col-span-7">
                        {sent ? (
                            <div
                                data-testid="contact-success"
                                role="status"
                                aria-live="polite"
                                className="rounded-3xl border border-emerald-200 bg-white p-8 shadow-[0_10px_30px_-5px_rgba(11,31,58,0.08)] lg:p-10"
                            >
                                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
                                    <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                                </div>
                                <h3 className="font-display mt-6 text-2xl font-extrabold tracking-tight text-[#0B1F3A] sm:text-3xl">
                                    Grazie, la sua richiesta è stata inviata.
                                </h3>
                                <p className="mt-4 text-base leading-relaxed text-[#475569]">
                                    La contatteremo nel più breve tempo possibile. Le abbiamo inviato anche un'email di conferma.
                                </p>
                                <p className="mt-3 text-base leading-relaxed text-[#475569]">
                                    Per urgenze può chiamarci allo{" "}
                                    <a href="tel:+390984443552" className="font-bold text-[#E30613] underline-offset-2 hover:underline">0984 443552</a>.
                                </p>
                                <button
                                    type="button"
                                    onClick={reset}
                                    data-testid="contact-send-another"
                                    className="mt-8 text-sm font-bold text-[#0B1F3A] underline decoration-[#E30613] decoration-2 underline-offset-4 hover:text-[#E30613]"
                                >
                                    Invia un'altra richiesta
                                </button>
                            </div>
                        ) : (
                        <form
                            onSubmit={submit}
                            noValidate
                            data-testid="contact-form"
                            className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_10px_30px_-5px_rgba(11,31,58,0.08)] lg:p-10"
                        >
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#0B1F3A]">
                                        Nome e Cognome *
                                    </label>
                                    <input
                                        data-testid="contact-name-input"
                                        value={form.nome}
                                        onChange={set("nome")}
                                        placeholder="Mario Rossi"
                                        className={inputCls}
                                    />
                                    <FieldError k="nome" />
                                </div>
                                <div>
                                    <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#0B1F3A]">
                                        Azienda
                                    </label>
                                    <input
                                        data-testid="contact-company-input"
                                        value={form.azienda}
                                        onChange={set("azienda")}
                                        placeholder="Nome attività"
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#0B1F3A]">
                                        Email *
                                    </label>
                                    <input
                                        data-testid="contact-email-input"
                                        type="text"
                                        inputMode="email"
                                        autoComplete="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="mario@azienda.it"
                                        className={inputCls}
                                    />
                                    <FieldError k="email" />
                                    {emailHint && !fieldErrors.email && (
                                        <p className="mt-1.5 text-xs text-[#4F5D70]" data-testid="contact-email-hint">
                                            Forse intendeva{" "}
                                            <button type="button" onClick={() => setForm({ ...form, email: emailHint })} className="font-bold text-[#E30613] underline underline-offset-2" data-testid="contact-email-hint-apply">{emailHint}</button>?
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#0B1F3A]">
                                        Telefono
                                    </label>
                                    <input
                                        data-testid="contact-phone-input"
                                        type="tel"
                                        value={form.telefono}
                                        onChange={set("telefono")}
                                        placeholder="+39 333 000 0000"
                                        className={inputCls}
                                    />
                                    <FieldError k="telefono" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#0B1F3A]">
                                        Divisione di interesse
                                    </label>
                                    <Select
                                        value={form.divisione}
                                        onValueChange={(v) => setForm({ ...form, divisione: v })}
                                    >
                                        <SelectTrigger
                                            data-testid="contact-division-select"
                                            className="h-[50px] rounded-xl border-slate-200 text-sm"
                                        >
                                            <SelectValue placeholder="Seleziona una divisione" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {DIVISIONI.map((d) => (
                                                <SelectItem
                                                    key={d}
                                                    value={d}
                                                    data-testid={`contact-division-option-${DIVISIONI.indexOf(d)}`}
                                                >
                                                    {d}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#0B1F3A]">
                                        Messaggio *
                                    </label>
                                    <textarea
                                        data-testid="contact-message-input"
                                        value={form.messaggio}
                                        onChange={set("messaggio")}
                                        rows={4}
                                        placeholder="Raccontaci la tua esigenza: tipo di impianto, ubicazione, tempistiche…"
                                        className={`${inputCls} resize-none`}
                                    />
                                    <FieldError k="messaggio" />
                                </div>
                            </div>
                            <label className="mt-6 flex items-start gap-3 text-sm text-[#0B1F3A]" data-testid="contact-privacy-label">
                                <input
                                    type="checkbox"
                                    required
                                    checked={privacyOk}
                                    onChange={(e) => setPrivacyOk(e.target.checked)}
                                    data-testid="contact-privacy-checkbox"
                                    className="mt-0.5 h-5 w-5 shrink-0 accent-[#E30613]"
                                />
                                <span>
                                    Ho letto la{" "}
                                    <Link to="/privacy-policy" target="_blank" className="font-bold text-[#E30613] underline-offset-2 hover:underline" data-testid="contact-privacy-link">
                                        Privacy Policy
                                    </Link>{" "}
                                    e acconsento al trattamento dei dati per essere ricontattato. *
                                </span>
                            </label>
                            {error && (
                                <p
                                    data-testid="contact-error"
                                    role="alert"
                                    className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-[#B91C1C]"
                                >
                                    {error}
                                </p>
                            )}
                            <button
                                type="submit"
                                data-testid="contact-submit-button"
                                disabled={sending}
                                className="group mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-[#E30613] px-8 py-4 text-base font-bold text-white transition-[background-color,box-shadow,transform] duration-300 hover:bg-[#C20410] hover:shadow-[0_16px_40px_-10px_rgba(227,6,19,0.5)] active:scale-[0.98] disabled:opacity-60 sm:w-auto"
                            >
                                {sending ? (
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                ) : (
                                    <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                )}
                                {sending ? "Invio in corso…" : "Invia Richiesta"}
                            </button>
                            <p className="mt-4 text-xs leading-relaxed text-[#4F5D70]">
                                I dati saranno utilizzati esclusivamente per ricontattarti, come
                                descritto nella Privacy Policy. Campi obbligatori contrassegnati con *.
                            </p>
                        </form>
                        )}
                    </Reveal>

                    <div className="flex flex-col gap-6 lg:col-span-5">
                        <Reveal delay={0.1}>
                            <div className="rounded-3xl bg-[#0B1F3A] p-8" data-testid="contact-info-card">
                                <h3 className="font-display text-lg font-bold text-white">
                                    FTM Group S.r.l.
                                </h3>
                                <ul className="mt-5 space-y-4 text-sm">
                                    <li className="flex items-start gap-3 text-white/80">
                                        <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#E30613]" />
                                        <span>
                                            Via Icaro, 2 — 87040
                                            <br />
                                            Castrolibero (CS), Italia
                                        </span>
                                    </li>
                                    <li className="flex items-center gap-3 text-white/80">
                                        <Phone className="h-5 w-5 shrink-0 text-[#E30613]" />
                                        <a
                                            href="tel:+390984443552"
                                            className="transition-colors hover:text-white"
                                            data-testid="contact-phone-link"
                                        >
                                            +39 0984 443552
                                        </a>
                                    </li>
                                    <li className="flex items-center gap-3 text-white/80">
                                        <Mail className="h-5 w-5 shrink-0 text-[#E30613]" />
                                        <a
                                            href="mailto:info@ftmgroup.it"
                                            className="transition-colors hover:text-white"
                                            data-testid="contact-email-link"
                                        >
                                            info@ftmgroup.it
                                        </a>
                                    </li>
                                    <li className="flex items-start gap-3 text-white/80">
                                        <Clock className="mt-0.5 h-5 w-5 shrink-0 text-[#E30613]" />
                                        <span data-testid="contact-hours">
                                            Lunedì–Venerdì
                                            <br />
                                            8:30–13:00 / 14:30–18:00
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </Reveal>
                        <Reveal delay={0.18} className="flex-1">
                            <div
                                className="h-full min-h-[280px] overflow-hidden rounded-3xl border border-slate-200 shadow-[0_10px_30px_-5px_rgba(11,31,58,0.08)]"
                                data-testid="contact-map"
                            >
                                {consent.media ? (
                                    <iframe
                                        title="FTM Group S.r.l. — Via Icaro 2, Castrolibero (CS)"
                                        src="https://www.google.com/maps?q=Via%20Icaro%202%2C%2087040%20Castrolibero%20CS%2C%20Italia&output=embed"
                                        className="h-full min-h-[280px] w-full border-0"
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                        allowFullScreen
                                    />
                                ) : (
                                    <div
                                        className="flex h-full min-h-[280px] flex-col items-center justify-center bg-[#0B1F3A] p-6 text-center text-white"
                                        data-testid="map-consent-gate"
                                    >
                                        <MapPin className="h-8 w-8 text-[#E30613]" />
                                        <p className="mt-3 text-sm font-bold">Accetta i cookie per vedere la mappa</p>
                                        <p className="mt-1 text-xs text-white/65">Mappa fornita da Google Maps (contenuto esterno).</p>
                                        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                                            <button type="button" onClick={openCookiePreferences} data-testid="map-consent-button" className="rounded-full bg-[#E30613] px-4 py-2 text-xs font-bold hover:bg-[#C20410]">
                                                Preferenze cookie
                                            </button>
                                            <a href="https://www.google.com/maps?q=Via+Icaro+2,+87040+Castrolibero+CS" target="_blank" rel="noopener noreferrer" className="rounded-full border border-white/30 px-4 py-2 text-xs font-bold hover:bg-white/10">
                                                Apri in Google Maps
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};
